import { useMutation } from "@apollo/client";
import { Chip, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { safeParseInt } from "encamp-shared/src/utils/safeParseNumber";
import pluralize from "pluralize";
import { useEffect, useState } from "react";

import { UPDATE_WORKFLOW_ESTIMATES_MUTATION } from "./api";
import { UnifiedWorkRow } from "./types";

const PRESETS: { label: string; minutes: number }[] = [
  { label: "15m", minutes: 15 },
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "2h", minutes: 120 },
  { label: "4h", minutes: 240 },
  { label: "1d", minutes: 480 },
];

/**
 * Set (or clear) the estimate override on one or many queue rows. Clearing
 * reverts the rows to their work type's templatized default estimate.
 */
export function WorkEstimateDialog({
  rows,
  open,
  onClose,
  onSaved,
}: {
  rows: UnifiedWorkRow[];
  open: boolean;
  onClose: () => void;
  /** Called after a successful save (refetch the queue here). */
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const [minutesText, setMinutesText] = useState("");
  const [updateEstimates, { loading }] = useMutation(UPDATE_WORKFLOW_ESTIMATES_MUTATION);

  // Seed from the selection when opening: a shared value is editable in
  // place; mixed values start blank.
  useEffect(() => {
    if (!open) {
      return;
    }
    const values = new Set(rows.map((row) => row.estimateMinutes ?? null));
    const only = values.size === 1 ? [...values][0] : null;
    setMinutesText(only !== null ? String(only) : "");
  }, [open, rows]);

  const parsedMinutes = safeParseInt(minutesText);
  const canSave = minutesText.trim() !== "" && parsedMinutes !== null && parsedMinutes >= 0;

  const apply = async (estimateMinutes: number | null) => {
    try {
      await updateEstimates({
        variables: { workflowIds: rows.map((row) => row.workflowId), estimateMinutes },
      });
      alerts.success(
        estimateMinutes === null
          ? `Cleared the estimate on ${rows.length} ${pluralize("work item", rows.length)}`
          : `Estimated ${rows.length} ${pluralize("work item", rows.length)} at ${estimateMinutes}m`
      );
      onSaved();
      onClose();
    } catch (err) {
      alerts.error("An error occurred while updating estimates", err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Set estimate (${rows.length} ${pluralize("item", rows.length)})`}
      primaryActionButtonProps={{
        saveText: "Save",
        loading,
        disabled: !canSave,
        onClick: () => {
          if (parsedMinutes !== null) {
            void apply(parsedMinutes);
          }
        },
      }}
      secondaryActionElements={[
        <Chip
          key="clear"
          label="Clear override (use work type default)"
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={() => void apply(null)}
        />,
      ]}
    >
      <Stack spacing={2} sx={{ minWidth: 360, pt: 0.5 }}>
        <Stack direction="row" spacing={1}>
          {PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              variant={parsedMinutes === preset.minutes ? "filled" : "outlined"}
              color={parsedMinutes === preset.minutes ? "primary" : "default"}
              onClick={() => setMinutesText(String(preset.minutes))}
            />
          ))}
        </Stack>
        <TextField
          autoFocus
          size="small"
          label="Minutes"
          value={minutesText}
          onChange={(event) => setMinutesText(event.target.value)}
          error={minutesText.trim() !== "" && !canSave}
          helperText={
            minutesText.trim() !== "" && !canSave ? "Enter a whole number of minutes" : " "
          }
        />
        <Typography variant="caption" color="text.secondary">
          Overrides the work type&apos;s default estimate for the selected items.
        </Typography>
      </Stack>
    </Dialog>
  );
}
